import {
  AuthorityType,
  BankAccountIdentifierType,
  DocumentContext,
  DocumentEntityType,
  DocumentType,
  PayoutPurposeCode
} from '@sollapay/enums';
import {FieldCombobox, FieldInput} from '@sollapay/ui/components';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {FormShell} from '@/components';
import {AiExtractionStatus} from '@/features/ai-extraction';
import {getLatestDocument, useDocumentsQuery} from '@/features/documents';
import {FeatureFlag, useFeatureFlag} from '@/features/feature-flags';
import {
  useBankBranchesQuery,
  useBankProvidersQuery,
  useBeneficiaryLastAccountQuery
} from '@/features/payments/hooks';
import {useApplyOnce} from '@/hooks';

import {AmountField} from './AmountField';
import {AccountTypeField} from './BankAccountFields';
import {CreatePayoutStepLayout} from './CreatePayoutStepLayout';
import {PayeeDocumentSection} from './PayeeDocumentSection';
import {PayeeSpecificFields} from './PayeeSpecificFields';
import {PurposeField} from './PurposeField';
import {ZahavOnlyNotice} from './ZahavOnlyNotice';
import {usePayoutAiFields} from '../hooks/usePayoutAiFields';
import {usePayoutTransferRules} from '../hooks/usePayoutTransferRules';
import {
  LIVE_ERROR_KEYS,
  buildAccountTypeOptions,
  getPayoutDetailsErrorKeys,
  isBankCheckPayout,
  resolveAutofilledAccountType,
  resolveReusableLastAccount,
  isAuthorityPayeeKind,
  resolvePayeeKind,
  withoutBankBrandAliases
} from '../utils/creditor-account.utils';
import {buildPayeeOptions, buildPayeeValueDisplay} from '../utils/payee-options.utils';
import {getPurposeCodeOptions} from '../utils/purpose-options.utils';

import type {CreditorAccountType, PayoutDetailsErrorKeys} from '../utils/creditor-account.utils';
import type {
  AuthorityDTO,
  BeneficiaryDTO,
  MunicipalAuthorityDTO,
  PaymentProviderDTO
} from '@sollapay/types';
import type {
  FieldComboboxOption,
  FieldComboboxOptionGroup,
  FieldSelectOption
} from '@sollapay/ui/components';
import type {FC} from 'react';
import type {UseFormGetValues, UseFormSetValue, UseFormWatch} from 'react-hook-form';

export interface CreatePayoutFormValues {
  beneficiaryId: string;
  amount: string;
  description: string;
  purposeCode: string;
  purposeOtherDetail: string;
  paymentReference: string;
  authorityName: string;
  voucherNumber: string;
  accountType: CreditorAccountType;
  accountNumber: string;
  /** The selected bank provider's id — drives the bank combobox; `bankCode` (the actual bank number) is resolved from it. */
  bankProviderId: string;
  bankCode: string;
  branchCode: string;
  bankAccountNumber: string;
  creditorDocumentId: string;
}

// Stable reference while the query loads — a fresh `[]` each render re-fires effects keyed on it.
const NO_BANK_PROVIDERS: PaymentProviderDTO[] = [];

export const CREATE_PAYOUT_FORM_DEFAULT_VALUES: CreatePayoutFormValues = {
  beneficiaryId: '',
  amount: '',
  description: '',
  purposeCode: '',
  purposeOtherDetail: '',
  paymentReference: '',
  authorityName: '',
  voucherNumber: '',
  accountType: BankAccountIdentifierType.BANK_DETAILS,
  accountNumber: '',
  bankProviderId: '',
  bankCode: '',
  branchCode: '',
  bankAccountNumber: '',
  creditorDocumentId: ''
};

export interface PaymentDetailsStepProps {
  trustAccountId: string;
  trustAccountName: string;
  getValues: UseFormGetValues<CreatePayoutFormValues>;
  setValue: UseFormSetValue<CreatePayoutFormValues>;
  watch: UseFormWatch<CreatePayoutFormValues>;
  beneficiaries: BeneficiaryDTO[];
  authorities: AuthorityDTO[];
  municipalAuthorities: MunicipalAuthorityDTO[];
  uploadSessionId: string;
  availableBalanceNis: string;
  isDirty: boolean;
  isSaveError: boolean;
  showErrors: boolean;
  onCancel: () => void;
  saveError?: unknown;
}

export const PaymentDetailsStep: FC<PaymentDetailsStepProps> = ({
  trustAccountId,
  trustAccountName,
  getValues,
  setValue,
  watch,
  beneficiaries,
  authorities,
  municipalAuthorities,
  uploadSessionId,
  availableBalanceNis,
  isDirty,
  isSaveError,
  showErrors,
  onCancel,
  saveError
}) => {
  const {t} = useTranslation('trustAccounts');
  const {t: tAuthorities} = useTranslation('authorities');
  const prefix = 'view.payouts.create.details';

  const {
    beneficiaryId,
    amount,
    description,
    purposeCode,
    purposeOtherDetail,
    paymentReference,
    authorityName,
    voucherNumber,
    accountType,
    accountNumber,
    bankProviderId,
    bankCode,
    branchCode,
    bankAccountNumber,
    creditorDocumentId
  } = watch();

  const payeeKind = resolvePayeeKind(beneficiaryId);
  const isBankCheck = isBankCheckPayout({beneficiaryId, accountType});
  const isCheckEnabled = useFeatureFlag(FeatureFlag.CHECK_PAYOUTS);
  const isAuthority = isAuthorityPayeeKind(payeeKind);
  const purposeCodeOptions = useMemo(() => getPurposeCodeOptions(payeeKind), [payeeKind]);
  const purposeCodeSelectOptions: FieldSelectOption[] = useMemo(
    () =>
      purposeCodeOptions.map(code => ({value: code, label: t(`${prefix}.purposeCode.${code}`)})),
    [purposeCodeOptions, t]
  );

  const setField = (field: keyof CreatePayoutFormValues, val: string) =>
    setValue(field, val, {shouldDirty: true});
  const setAmount = (val: string) => setField('amount', val);
  const setDescription = (val: string) => setField('description', val);
  const setPurposeCode = (val: string) => {
    setField('purposeCode', val);
    if (val !== PayoutPurposeCode.OTHER) setField('purposeOtherDetail', '');
  };
  const setPurposeOtherDetail = (val: string) => setField('purposeOtherDetail', val);
  const setPaymentReference = (val: string) => setField('paymentReference', val);
  const setAuthorityName = (val: string) => setField('authorityName', val);
  const setVoucherNumber = (val: string) => setField('voucherNumber', val);
  const setBankAccountNumber = (val: string) => setField('bankAccountNumber', val);
  const setAccountNumber = (val: string) => setField('accountNumber', val);

  const {data: allBankProviders = NO_BANK_PROVIDERS} = useBankProvidersQuery();
  const bankProviders = useMemo(
    () => withoutBankBrandAliases(allBankProviders),
    [allBankProviders]
  );
  const selectedBankProvider = useMemo(
    () => bankProviders.find(provider => provider.id === bankProviderId),
    [bankProviders, bankProviderId]
  );
  const hasBranchLookup = selectedBankProvider != null && !selectedBankProvider.isFakeBank;
  const branchLookupCode =
    hasBranchLookup && selectedBankProvider.bankNumber != null
      ? String(selectedBankProvider.bankNumber)
      : '';
  const {data: bankBranches = [], isLoading: isBranchesLoading} =
    useBankBranchesQuery(branchLookupCode);

  const handleBankProviderChange = (providerId: string) => {
    const provider = bankProviders.find(p => p.id === providerId);
    setField('bankProviderId', providerId);
    setField('bankCode', provider?.bankNumber != null ? String(provider.bankNumber) : '');
  };

  const handleBeneficiaryChange = (val: string) => {
    setField('beneficiaryId', val);
    setPurposeCode('');
  };

  const creditorDocumentMetadata = useMemo(
    () => ({partyId: beneficiaryId, sessionId: uploadSessionId}),
    [beneficiaryId, uploadSessionId]
  );
  const documentScopes = useMemo(
    () => [{id: 'creditor', metadata: creditorDocumentMetadata}],
    [creditorDocumentMetadata]
  );

  // The bank confirmation belongs to the beneficiary, not this payout — once linked from a
  // prior payout it's reused here instead of asking to upload it again. Authority payees never
  // reuse a prior document — each voucher/demand is specific to this payout.
  const linkedCreditorDocumentFilter = useMemo(
    () => ({partyId: beneficiaryId, sessionId: undefined}),
    [beneficiaryId]
  );
  const {data: payoutDocuments} = useDocumentsQuery(
    DocumentEntityType.TRUST_ACCOUNT,
    trustAccountId,
    DocumentContext.PAYOUTS
  );
  const linkedCreditorDocument = useMemo(
    () =>
      payeeKind === 'beneficiary'
        ? getLatestDocument(
            payoutDocuments,
            DocumentType.BANK_ACCOUNT_CONFIRMATION,
            linkedCreditorDocumentFilter
          )
        : undefined,
    [payoutDocuments, linkedCreditorDocumentFilter, payeeKind]
  );
  const creditorDocumentMetadataFilter = linkedCreditorDocument
    ? linkedCreditorDocumentFilter
    : creditorDocumentMetadata;

  // A reused confirmation doc means the bank account was already confirmed on a prior payout
  // to this beneficiary — reuse it too, and lock the fields until the doc is removed.
  const {data: lastAccount} = useBeneficiaryLastAccountQuery(trustAccountId, beneficiaryId, {
    enabled: !!linkedCreditorDocument
  });
  const reusableAccount = resolveReusableLastAccount(lastAccount);
  const isAccountLocked = !!linkedCreditorDocument && !!reusableAccount;

  useApplyOnce(reusableAccount ? `${beneficiaryId}:${reusableAccount.accountNumber}` : null, () => {
    if (!reusableAccount) return;
    setValue(
      'accountType',
      resolveAutofilledAccountType(getValues('accountType'), BankAccountIdentifierType.IBAN),
      {shouldDirty: true}
    );
    setValue('accountNumber', reusableAccount.accountNumber, {shouldDirty: true});
  });

  const errorKeys = getPayoutDetailsErrorKeys(
    {
      beneficiaryId,
      amount,
      description,
      purposeCode,
      purposeOtherDetail,
      voucherNumber,
      authorityName,
      paymentReference,
      accountType,
      accountNumber,
      bankCode,
      branchCode,
      bankAccountNumber,
      creditorDocumentId
    },
    availableBalanceNis
  );

  const {
    isProcessing,
    fieldAi,
    isFlashing,
    stopAllScopes,
    displaySuggestionsCount,
    isExtractionPartial,
    mismatchedExtractedAmount
  } = usePayoutAiFields({
    trustAccountId,
    payeeKind,
    isAuthority,
    isDirty,
    isSaveError,
    onCancel,
    getValues,
    setValue,
    documentScopes,
    creditorDocumentId,
    amount,
    bankProviders,
    errorKeys
  });

  // After usePayoutAiFields: its account-type autofill must not undo the zahav-only switch.
  const {isZahavOnly, isZahavLocked} = usePayoutTransferRules({
    payeeKind,
    amount,
    accountType,
    isAccountLocked,
    isCheckEnabled,
    bankProviders,
    getValues,
    setValue
  });

  const payeeGroups: FieldComboboxOptionGroup[] = useMemo(
    () => buildPayeeOptions({beneficiaries, authorities, t, tAuthorities}),
    [beneficiaries, authorities, t, tAuthorities]
  );
  const payeeValueDisplay = useMemo(
    () => buildPayeeValueDisplay({beneficiaries, authorities, t, tAuthorities}),
    [beneficiaries, authorities, t, tAuthorities]
  );

  const municipalAuthorityOptions: FieldComboboxOption[] = useMemo(
    () =>
      municipalAuthorities.map(authority => ({
        value: authority.name,
        label: authority.name,
        searchText: authority.name
      })),
    [municipalAuthorities]
  );

  const accountTypeOptions = useMemo(
    () => buildAccountTypeOptions(t, {isZahavOnly, isCheckEnabled}),
    [t, isZahavOnly, isCheckEnabled]
  );

  const bankProviderOptions = useMemo(
    () =>
      bankProviders
        .filter(provider => !provider.isFakeBank && provider.bankNumber !== null)
        .sort((a, b) => (a.bankNumber ?? 0) - (b.bankNumber ?? 0)),
    [bankProviders]
  );

  const errorText = (field: keyof PayoutDetailsErrorKeys) => {
    const key = errorKeys[field];
    if (!key) return undefined;
    if (!showErrors && !LIVE_ERROR_KEYS.has(key)) return undefined;
    return t(`${prefix}.errors.${key}`);
  };

  return (
    <CreatePayoutStepLayout title={t(`${prefix}.title`)} subtitle={t(`${prefix}.subtitle`)}>
      {/* No py-6 here — CreatePayoutStepLayout owns the step's vertical padding. */}
      <FormShell
        id="payout-payment-details-form"
        className="flex flex-col gap-6"
        onSubmit={e => e.preventDefault()}
        saveError={saveError}
      >
        <FieldInput
          label={t(`${prefix}.trustAccountName`)}
          value={trustAccountName}
          disabled
          readOnly
        />

        <FieldCombobox
          label={t(`${prefix}.beneficiary`)}
          placeholder={t(`${prefix}.beneficiaryPlaceholder`)}
          groups={payeeGroups}
          value={beneficiaryId}
          onValueChange={handleBeneficiaryChange}
          renderValue={payeeValueDisplay}
          emptyText={t(`${prefix}.beneficiaryEmpty`)}
          required
        />

        {payeeKind !== 'none' && payeeKind !== AuthorityType.TAX_AUTHORITY && (
          <AccountTypeField
            accountType={accountType}
            accountTypeOptions={accountTypeOptions}
            isZahavOnly={isZahavLocked}
            fieldAi={fieldAi}
            onAccountTypeChange={val => setField('accountType', val)}
          />
        )}

        {/* A bank check needs no supporting document. */}
        {!isBankCheck && (
          <>
            <PayeeDocumentSection
              payeeKind={payeeKind}
              trustAccountId={trustAccountId}
              metadata={creditorDocumentMetadata}
              metadataFilter={creditorDocumentMetadataFilter}
              hasLinkedCreditorDocument={!!linkedCreditorDocument}
              error={errorText('document')}
              onDocumentUploaded={documentId =>
                setValue('creditorDocumentId', documentId, {
                  shouldDirty: true,
                  shouldValidate: true
                })
              }
              onDocumentsLoaded={documentIds =>
                setValue('creditorDocumentId', documentIds[0] ?? '', {shouldValidate: true})
              }
            />

            {/* aiFields is keyed by scope id, not by document — it doesn't clear itself when the
            document that produced it is deleted, so gate the banner on a document still existing. */}
            <AiExtractionStatus
              isProcessing={isProcessing && !!creditorDocumentId}
              suggestionsCount={creditorDocumentId ? displaySuggestionsCount : 0}
              isPartial={isExtractionPartial}
              onFillManually={stopAllScopes}
            />
          </>
        )}

        <PurposeField
          payeeKind={payeeKind}
          description={description}
          onDescriptionChange={setDescription}
          purposeCode={purposeCode}
          onPurposeCodeChange={setPurposeCode}
          purposeOtherDetail={purposeOtherDetail}
          onPurposeOtherDetailChange={setPurposeOtherDetail}
          purposeCodeOptions={purposeCodeSelectOptions}
          isFlashing={isFlashing}
          fieldAi={fieldAi}
          descriptionError={errorText('description')}
          purposeCodeError={errorText('purposeCode')}
          purposeOtherDetailError={errorText('purposeOtherDetail')}
        />

        <AmountField
          amount={amount}
          onAmountChange={setAmount}
          isAuthority={isAuthority}
          isFlashing={isFlashing}
          fieldAi={fieldAi}
          error={errorText('amount')}
          mismatchedExtractedAmount={mismatchedExtractedAmount}
          availableBalanceNis={availableBalanceNis}
        />

        {isZahavLocked && <ZahavOnlyNotice />}

        <PayeeSpecificFields
          payeeKind={payeeKind}
          accountType={accountType}
          bankProviderId={bankProviderId}
          branchCode={branchCode}
          bankAccountNumber={bankAccountNumber}
          accountNumber={accountNumber}
          isAccountLocked={isAccountLocked}
          isBranchesLoading={isBranchesLoading}
          hasBranchLookup={hasBranchLookup}
          bankProviders={bankProviderOptions}
          bankBranches={bankBranches}
          isFlashing={isFlashing}
          fieldAi={fieldAi}
          onBankProviderChange={handleBankProviderChange}
          onBranchCodeChange={val => setField('branchCode', val)}
          onBankAccountNumberChange={setBankAccountNumber}
          onAccountNumberChange={setAccountNumber}
          accountNumberError={errorText('accountNumber')}
          bankCodeError={errorText('bankCode')}
          branchCodeError={errorText('branchCode')}
          bankAccountNumberError={errorText('bankAccountNumber')}
          authorityName={authorityName}
          onAuthorityNameChange={setAuthorityName}
          municipalAuthorityOptions={municipalAuthorityOptions}
          authorityNameError={errorText('authorityName')}
          paymentReference={paymentReference}
          onPaymentReferenceChange={setPaymentReference}
          paymentReferenceError={errorText('paymentReference')}
          voucherNumber={voucherNumber}
          onVoucherNumberChange={setVoucherNumber}
          voucherNumberError={errorText('voucherNumber')}
        />
      </FormShell>
    </CreatePayoutStepLayout>
  );
};
